import type { Shift } from "@domain/CRM/shift";
import { buildShiftJobUpdates } from "@domain/CRM/shift";
import type { IShifts } from "@ports/shifts/IShifts";

export interface ShiftsUseCasesDeps {
	shiftsApi: IShifts;
}

/** One batch of staged scheduling edits to persist. */
export interface SaveScheduleChangesInput {
	/** Brand-new shifts to create (already mapped from the UI's pending drafts to Shift fields). */
	creates: Partial<Shift>[];
	/** The full set of existing shifts; used to resolve which ones the staged maps touch. */
	existingShifts: Shift[];
	/** shiftId → jobIds to add. */
	additions: ReadonlyMap<string, ReadonlySet<string>>;
	/** shiftId → jobIds to remove. */
	removals: ReadonlyMap<string, ReadonlySet<string>>;
}

/**
 * Write path for the scheduling board. Encapsulates the "save staged shift edits" orchestration
 * so VMs depend on this use case rather than `API.Data.Shifts` + the merge rule inline. The
 * `(current ∪ additions) \ removals` math lives in the `@domain/CRM/shift` `buildShiftJobUpdates`
 * operation; this use case just sequences the create + update calls through the `IShifts` port.
 */
export interface ShiftsUseCases {
	/** Create the pending shifts and update every existing shift whose job set changed. Each call
	 *  no-ops the half (create / update) it has nothing for. */
	saveScheduleChanges(input: SaveScheduleChangesInput): Promise<void>;
}

export async function saveScheduleChanges(deps: ShiftsUseCasesDeps, input: SaveScheduleChangesInput): Promise<void> {
	const updates = buildShiftJobUpdates(input.existingShifts, input.additions, input.removals);
	await Promise.all([
		input.creates.length > 0 ? deps.shiftsApi.createMany(input.creates) : Promise.resolve([]),
		updates.length > 0 ? deps.shiftsApi.updateMany(updates) : Promise.resolve([]),
	]);
}

export function makeShiftsUseCases(deps: ShiftsUseCasesDeps): ShiftsUseCases {
	return {
		saveScheduleChanges: (input) => saveScheduleChanges(deps, input),
	};
}
